import { IS_ENCRYPTION_ENABLED } from "@/constants/app";
import { useAuthStore } from "@/store/useAuthStore";
import { useLoaderStore } from "@/store/useLoaderStore";
import { decrypt } from "@/utils/cipher";
import { toast } from "sonner";

let isHandlingUnauthorized = false;
let lastNetworkErrorToast = 0;

const responseInterceptor = {
  success: async (response: any) => {
    try {
      // Check if response has data field
      if (response.data?.data) {
        if (IS_ENCRYPTION_ENABLED) {
          // When encryption is ON: response data is encrypted, decrypt it
          try {
            const decryptedData = await decrypt(response.data.data);
            response.data.data = JSON.parse(decryptedData);
          } catch (error) {
            console.error("Error decrypting response data:", error);
            // If decryption fails, try to parse as JSON
            response.data.data = JSON.parse(response.data.data);
          }
        } else {
          // When encryption is OFF: response data is already decrypted by server
          // Just parse it if it's a string
          if (typeof response.data.data === 'string') {
            response.data.data = JSON.parse(response.data.data);
          }
        }
      }
    } catch (error) {
      console.error("Error processing response data:", error);
    } finally {
      // Hide loader after successful response
      useLoaderStore.getState().hideLoader();
    }
    return response;
  },

  error: (error: any) => {
    console.error("API Error:", error);

    // Hide loader on error
    useLoaderStore.getState().hideLoader();

    // Check for network/internet disconnection errors
    const isNetworkError =
      !error.response &&
      (error.code === "ERR_NETWORK" ||
        error.code === "ERR_INTERNET_DISCONNECTED" ||
        error.message?.includes("Network Error") ||
        error.message?.includes("ERR_INTERNET_DISCONNECTED"));

    if (isNetworkError) {
      const now = Date.now();
      // Throttle toast to once every 5 seconds
      if (now - lastNetworkErrorToast > 5000) {
        lastNetworkErrorToast = now;
        toast.error(
          "Internet connection has been disconnected. Please try again later.",
        );
      }
    }

    // Handle 403 Forbidden - redirect to 403 error page
    if (error.response?.status === 403) {
      window.location.href = "/403";
      return Promise.reject(error);
    }

    // Handle 401 Unauthorized - perform logout and redirect to login
    if (error.response?.status === 401 && !isHandlingUnauthorized) {
      const { logout, isLoggingOut } = useAuthStore.getState();
      if (!isLoggingOut) {
        isHandlingUnauthorized = true;
        logout()
          .catch((logoutError) =>
            console.error("Auto-logout failed", logoutError),
          )
          .finally(() => {
            isHandlingUnauthorized = false;
          });
      }
    }

    return Promise.reject(error);
  },
};

export default responseInterceptor;
